// useState removed - unused
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreVertical, ExternalLink, Calendar, Users, CheckSquare, MessageSquare, Paperclip } from 'lucide-react';
import type { Task } from '@/types/task';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface TasksListViewProps {
  tasks: Task[];
  loading?: boolean;
  onTaskClick: (taskId: string) => void;
  selectedRowIds: Set<string>;
  onSelectionChange: (taskId: string, checked: boolean) => void;
  visibleColumns?: string[];
}

export function TasksListView({ tasks, loading, onTaskClick, selectedRowIds, onSelectionChange, visibleColumns = ['title', 'type', 'priority', 'status', 'assignee', 'dueDate', 'links', 'progress'] }: Readonly<TasksListViewProps>) {
  const navigate = useNavigate();
  
  // Helper to check if a column is visible
  const isColumnVisible = (columnId: string) => visibleColumns.includes(columnId);

  const handleSelectAll = (checked: boolean) => {
    tasks.forEach(task => {
      onSelectionChange(task.id, checked);
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'P0': return 'bg-red-100 text-red-800 border-red-200';
      case 'P1': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'P2': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'P3': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'In Progress': return 'bg-purple-100 text-purple-800';
      case 'Blocked': return 'bg-red-100 text-red-800';
      case 'Resolved': return 'bg-green-100 text-green-800';
      case 'Closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Bug': return 'bg-red-50 text-red-700 border-red-200';
      case 'Feature': return 'bg-green-50 text-green-700 border-green-200';
      case 'Task': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Maintenance': return 'bg-orange-50 text-orange-700 border-orange-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const formatDueDate = (dueDate?: string) => {
    if (!dueDate) return null;
    
    const date = new Date(dueDate);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const isOverdue = date < today;
    const isToday = date.toDateString() === today.toDateString();
    const isTomorrow = date.toDateString() === tomorrow.toDateString();
    
    let label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (isToday) label = 'Today';
    else if (isTomorrow) label = 'Tomorrow';
    
    return {
      label,
      isOverdue,
      isToday,
      isTomorrow,
    };
  };

  const getChecklistProgress = (task: Task) => {
    if (task.checklists.length === 0) return null;
    const completed = task.checklists.filter(c => c.completed).length;
    const total = task.checklists.length;
    const percentage = Math.round((completed / total) * 100);
    return { completed, total, percentage };
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        {[...Array(10)].map((_, i) => (
          <Skeleton key={`skeleton-${i}`} className="h-16" />
        ))}
      </div>
    );
  }

  if (!tasks || tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-lg font-medium text-muted-foreground">No tasks found</p>
          <p className="text-sm text-muted-foreground mt-1">
            Try adjusting your filters or create a new task
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      <Table>
        <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
          <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
            <TableHead className="h-12 font-semibold w-[60px]">
              <div className="flex items-center justify-center">
                <Checkbox
                  checked={tasks && selectedRowIds.size === tasks.length && tasks.length > 0}
                  onCheckedChange={handleSelectAll}
                />
              </div>
            </TableHead>
            {isColumnVisible('title') && (
              <TableHead className="h-12 font-semibold min-w-[300px]">Title</TableHead>
            )}
            {isColumnVisible('type') && (
              <TableHead className="h-12 font-semibold w-[130px]">Type</TableHead>
            )}
            {isColumnVisible('priority') && (
              <TableHead className="h-12 font-semibold w-[100px]">Priority</TableHead>
            )}
            {isColumnVisible('status') && (
              <TableHead className="h-12 font-semibold w-[120px]">Status</TableHead>
            )}
            {isColumnVisible('assignee') && (
              <TableHead className="h-12 font-semibold w-[180px]">Assignee</TableHead>
            )}
            {isColumnVisible('dueDate') && (
              <TableHead className="h-12 font-semibold w-[120px]">Due Date</TableHead>
            )}
            {isColumnVisible('links') && (
              <TableHead className="h-12 font-semibold w-[200px]">Links</TableHead>
            )}
            {isColumnVisible('progress') && (
              <TableHead className="h-12 font-semibold w-[150px]">Progress</TableHead>
            )}
            <TableHead className="h-12 font-semibold w-[60px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tasks.map((task, index) => {
            const dueDate = formatDueDate(task.dueDate);
            const progress = getChecklistProgress(task);
            
            return (
              <TableRow
                key={task.id}
                className={`
                  ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                  hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800 cursor-pointer
                `}
                onClick={() => onTaskClick(task.id)}
              >
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-center">
                    <Checkbox
                      checked={selectedRowIds.has(task.id)}
                      onCheckedChange={(checked) => onSelectionChange(task.id, checked as boolean)}
                    />
                  </div>
                </TableCell>
                {isColumnVisible('title') && (
                  <TableCell className="px-4 py-3">
                    <div className="space-y-1">
                      <div className="font-medium text-primary hover:underline cursor-pointer">{task.title}</div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                        {task.commentsCount > 0 && (
                          <span className="flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />
                            {task.commentsCount}
                          </span>
                        )}
                        {task.attachmentsCount > 0 && (
                          <span className="flex items-center gap-1">
                            <Paperclip className="h-3 w-3" />
                            {task.attachmentsCount}
                          </span>
                        )}
                        {task.subtasks.length > 0 && (
                          <span className="flex items-center gap-1">
                            <CheckSquare className="h-3 w-3" />
                            {task.subtasks.filter(s => s.status === 'Resolved').length}/{task.subtasks.length}
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                )}
                {isColumnVisible('type') && (
                  <TableCell className="px-4 py-3">
                    <Badge variant="outline" className={cn('border', getTypeColor(task.type))}>
                      {task.type}
                    </Badge>
                  </TableCell>
                )}
                {isColumnVisible('priority') && (
                  <TableCell className="px-4 py-3">
                    <Badge variant="outline" className={cn('border font-semibold', getPriorityColor(task.priority))}>
                      {task.priority}
                    </Badge>
                  </TableCell>
                )}
                {isColumnVisible('status') && (
                  <TableCell className="px-4 py-3">
                    <Badge className={getStatusColor(task.status)}>
                      {task.status}
                    </Badge>
                  </TableCell>
                )}
                {isColumnVisible('assignee') && (
                  <TableCell className="px-4 py-3">
                    {task.assignee ? (
                      <div className="flex items-center gap-2">
                        {task.assignee.type === 'user' ? (
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={task.assignee.avatar} />
                            <AvatarFallback className="text-xs">
                              {task.assignee.name.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                        ) : (
                          <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                            <Users className="h-3 w-3 text-primary" />
                          </div>
                        )}
                        <span className="text-sm truncate">{task.assignee.name}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-slate-500 dark:text-slate-400">Unassigned</span>
                    )}
                  </TableCell>
                )}
                {isColumnVisible('dueDate') && (
                  <TableCell className="px-4 py-3">
                    {dueDate ? (
                      <div className="flex items-center gap-2">
                        <Calendar className={cn(
                          'h-3 w-3',
                          dueDate.isOverdue && 'text-red-600',
                          !dueDate.isOverdue && dueDate.isToday && 'text-orange-600',
                          !dueDate.isOverdue && !dueDate.isToday && 'text-muted-foreground'
                        )} />
                        <span className={cn(
                          'text-sm',
                          dueDate.isOverdue && 'text-red-600 font-medium',
                          !dueDate.isOverdue && dueDate.isToday && 'text-orange-600 font-medium',
                          !dueDate.isOverdue && !dueDate.isToday && 'text-muted-foreground'
                        )}>
                          {dueDate.label}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-slate-500 dark:text-slate-400">No due date</span>
                    )}
                  </TableCell>
                )}
                {isColumnVisible('links') && (
                  <TableCell className="px-4 py-3">
                    {task.links.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {task.links.slice(0, 2).map((link) => (
                          <Badge
                            key={link.id}
                            variant="outline"
                            className="text-xs"
                          >
                            {link.type === 'asset' && '📦'}
                            {link.type === 'location' && '📍'}
                            {link.type === 'audit' && '📋'}
                            {link.type === 'exception' && '⚠️'}
                            <span className="ml-1 truncate max-w-[80px]">{link.entityName}</span>
                          </Badge>
                        ))}
                        {task.links.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{task.links.length - 2}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-slate-500 dark:text-slate-400">-</span>
                    )}
                  </TableCell>
                )}
                {isColumnVisible('progress') && (
                  <TableCell className="px-4 py-3">
                    {progress ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all"
                            style={{ width: `${progress.percentage}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {progress.completed}/{progress.total}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-slate-500 dark:text-slate-400">-</span>
                    )}
                  </TableCell>
                )}
                <TableCell className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/tasks/${task.id}`)}>
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Open
                      </DropdownMenuItem>
                      <DropdownMenuItem>Edit</DropdownMenuItem>
                      <DropdownMenuItem>Assign</DropdownMenuItem>
                      <DropdownMenuItem>Change Status</DropdownMenuItem>
                      <DropdownMenuItem className="text-red-600">Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
